/**
 * Queue Store - Sagas
 */

import { call, put, takeLatest, all } from '@ufc/core'
import { extractErrorMessage } from '@ufc/core'
import type { PayloadAction } from '@ufc/core'
import { queueActions } from './queue.slice'
import { queueApi, referenceDataApi } from '../infrastructure'
import {
  transformDtosToLoadingLocations,
  transformDtoToLoadingLocation,
  transformDtosToQueueEntries,
  transformDtoToQueueEntry,
  transformDtosToLoadingLocationRules,
  transformDtoToLoadingLocationRule,
} from '../api'
import type {
  LoadingLocationFilter,
  LoadingLocationFormData,
  LoadingLocationRuleFilter,
  LoadingLocationRuleFormData,
  QueueEntryFilter,
  JoinQueueData,
} from '../domain/types'

// ============================================================================
// Loading Location Sagas
// ============================================================================

function* fetchLoadingLocationsSaga(
  action: PayloadAction<LoadingLocationFilter | undefined>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocations.getAll,
      action.payload
    )
    const locations = transformDtosToLoadingLocations(response.data || [])
    yield put(queueActions.fetchLoadingLocationsSuccess(locations))
  } catch (error: any) {
    yield put(
      queueActions.fetchLoadingLocationsFailure(
        extractErrorMessage(error) || 'queue.errors.fetchLocationsFailed'
      )
    )
  }
}

function* fetchLoadingLocationSaga(action: PayloadAction<string>): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocations.getById,
      action.payload
    )
    const location = transformDtoToLoadingLocation(response.data)
    yield put(queueActions.fetchLoadingLocationSuccess(location))
  } catch (error: any) {
    yield put(
      queueActions.fetchLoadingLocationFailure(
        extractErrorMessage(error) || 'queue.errors.fetchLocationFailed'
      )
    )
  }
}

function* createLoadingLocationSaga(
  action: PayloadAction<LoadingLocationFormData>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocations.create,
      action.payload
    )
    const location = transformDtoToLoadingLocation(response.data)
    yield put(queueActions.createLoadingLocationSuccess(location))
  } catch (error: any) {
    yield put(
      queueActions.createLoadingLocationFailure(
        extractErrorMessage(error) || 'queue.errors.createLocationFailed'
      )
    )
  }
}

function* updateLoadingLocationSaga(
  action: PayloadAction<{ id: string; data: Partial<LoadingLocationFormData> }>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocations.update,
      action.payload.id,
      action.payload.data
    )
    const location = transformDtoToLoadingLocation(response.data)
    yield put(queueActions.updateLoadingLocationSuccess(location))
  } catch (error: any) {
    yield put(
      queueActions.updateLoadingLocationFailure(
        extractErrorMessage(error) || 'queue.errors.updateLocationFailed'
      )
    )
  }
}

function* deleteLoadingLocationSaga(action: PayloadAction<string>): Generator {
  try {
    yield call(queueApi.loadingLocations.delete, action.payload)
    yield put(queueActions.deleteLoadingLocationSuccess(action.payload))
  } catch (error: any) {
    yield put(
      queueActions.deleteLoadingLocationFailure(
        extractErrorMessage(error) || 'queue.errors.deleteLocationFailed'
      )
    )
  }
}

// ============================================================================
// Queue Entry Sagas
// ============================================================================

function* fetchQueueEntriesSaga(
  action: PayloadAction<QueueEntryFilter | undefined>
): Generator {
  try {
    const response: any = yield call(
      queueApi.queueEntries.getAll,
      action.payload
    )
    const entries = transformDtosToQueueEntries(response.data || [])
    yield put(queueActions.fetchQueueEntriesSuccess(entries))
  } catch (error: any) {
    yield put(
      queueActions.fetchQueueEntriesFailure(
        extractErrorMessage(error) || 'queue.errors.fetchEntriesFailed'
      )
    )
  }
}

function* fetchExpiredEntriesTodaySaga(
  action: PayloadAction<QueueEntryFilter | undefined>
): Generator {
  try {
    const filter: QueueEntryFilter = {
      search: '',
      status: 'expired',
      ...(action.payload ?? {}),
    }
    const response: any = yield call(queueApi.queueEntries.getAll, filter)
    const entries = transformDtosToQueueEntries(response.data || [])
    yield put(queueActions.fetchExpiredEntriesTodaySuccess(entries))
  } catch {
    yield put(queueActions.fetchExpiredEntriesTodayFailure())
  }
}

function* fetchQueueEntrySaga(action: PayloadAction<string>): Generator {
  try {
    const response: any = yield call(
      queueApi.queueEntries.getById,
      action.payload
    )
    const entry = transformDtoToQueueEntry(response.data)
    yield put(queueActions.fetchQueueEntrySuccess(entry))
  } catch (error: any) {
    yield put(
      queueActions.fetchQueueEntryFailure(
        extractErrorMessage(error) || 'queue.errors.fetchEntryFailed'
      )
    )
  }
}

// ============================================================================
// Join Queue Saga
// ============================================================================

function* joinQueueSaga(action: PayloadAction<JoinQueueData>): Generator {
  try {
    const response: any = yield call(queueApi.queueEntries.join, action.payload)
    const result = {
      queueEntry: transformDtoToQueueEntry(response.data.queueEntry),
      position: response.data.position,
      estimatedWaitTime: response.data.estimatedWaitTime,
      instructions: response.data.instructions || [],
    }
    yield put(queueActions.joinQueueSuccess(result))
  } catch (error: any) {
    yield put(
      queueActions.joinQueueFailure(extractErrorMessage(error) || 'queue.errors.joinFailed')
    )
  }
}

// ============================================================================
// Cancel Entry Saga
// ============================================================================

function* cancelEntrySaga(
  action: PayloadAction<{ id: string; reason?: string }>
): Generator {
  try {
    const response: any = yield call(
      queueApi.queueEntries.cancel,
      action.payload.id,
      action.payload.reason
    )
    const entry = transformDtoToQueueEntry(response.data)
    yield put(queueActions.cancelEntrySuccess(entry))
  } catch (error: any) {
    yield put(
      queueActions.cancelEntryFailure(
        extractErrorMessage(error) || 'queue.errors.cancelFailed'
      )
    )
  }
}

// ============================================================================
// Dashboard Saga
// ============================================================================

function* fetchDashboardSaga(
  action: PayloadAction<{ tenantId?: string; factoryId?: string } | undefined>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocations.getDashboard,
      action.payload?.tenantId,
      action.payload?.factoryId
    )

    const locations =
      response.data?.map((loc: any) => ({
        loadingLocation: transformDtoToLoadingLocation(loc),
        queueLength: loc.queueStats?.totalInQueue || 0,
        activeInside: loc.queueStats?.activeInside || 0,
        waiting: loc.queueStats?.waiting || 0,
        availableSlots: loc.queueStats?.availableSlots || 0,
        isFull: loc.queueStats?.isFull || false,
      })) || []

    const dashboard = {
      locations,
      stats: {
        totalLocations: response.meta?.total || locations.length,
        activeLocations: locations.filter(
          (l: any) => l.loadingLocation.isActive
        ).length,
        totalCapacity: response.meta?.totalCapacity || 0,
        totalOccupied: response.meta?.totalOccupied || 0,
        totalWaiting: locations.reduce(
          (sum: number, l: any) => sum + l.waiting,
          0
        ),
        totalInQueue: locations.reduce(
          (sum: number, l: any) => sum + l.queueLength,
          0
        ),
      },
    }

    yield put(queueActions.fetchDashboardSuccess(dashboard))
  } catch (error: any) {
    yield put(
      queueActions.fetchDashboardFailure(
        extractErrorMessage(error) || 'queue.errors.fetchDashboardFailed'
      )
    )
  }
}

// ============================================================================
// Recent Events Saga
// ============================================================================

function* fetchRecentEventsSaga(
  action: PayloadAction<{ limit?: number; tenantId?: string } | undefined>
): Generator {
  try {
    const response: any = yield call(
      queueApi.queueEvents.getRecent,
      action.payload?.limit,
      action.payload?.tenantId
    )
    yield put(queueActions.fetchRecentEventsSuccess(response.data || []))
  } catch (error: any) {
    yield put(
      queueActions.fetchRecentEventsFailure(
        extractErrorMessage(error) || 'queue.errors.fetchEventsFailed'
      )
    )
  }
}

// ============================================================================
// Refresh Queue Saga
// ============================================================================

function* refreshQueueSaga(action: PayloadAction<string>): Generator {
  try {
    const response: any = yield call(
      queueApi.queueEntries.refreshQueue,
      action.payload
    )
    yield put(
      queueActions.refreshQueueSuccess({
        locationId: action.payload,
        promotedCount: response.data?.promotedCount || 0,
      })
    )
    yield put(
      queueActions.fetchQueueEntries({
        search: '',
        status: 'active',
        loadingLocationId: action.payload,
      })
    )
  } catch (error: any) {
    yield put(
      queueActions.refreshQueueFailure(
        extractErrorMessage(error) || 'queue.errors.refreshFailed'
      )
    )
  }
}

// ============================================================================
// Reference Data Saga
// ============================================================================

function* fetchReferenceDataSaga(): Generator {
  try {
    const [variantsList, factoriesList]: any[] = yield all([
      call([referenceDataApi, referenceDataApi.getVariants]),
      call([referenceDataApi, referenceDataApi.getFactories]),
    ])

    const variants = (variantsList ?? []).map((v: any) => ({
      documentId: v.documentId,
      name: typeof v.name === 'object'
        ? (v.name?.en ?? v.name?.ar ?? v.variantCode ?? v.documentId)
        : (v.name ?? v.variantCode ?? v.documentId),
      code: v.variantCode ?? v.code,
      packaging: v.packaging,
    }))

    const factories = (factoriesList ?? []).map((f: any) => ({
      documentId: f.documentId,
      name: typeof f.name === 'object'
        ? (f.name?.en ?? f.name?.ar ?? f.documentId)
        : (f.name ?? f.documentId),
    }))

    yield put(queueActions.fetchReferenceDataSuccess({ variants, factories }))
  } catch (err) {
    yield put(queueActions.fetchReferenceDataFailure())
  }
}

// ============================================================================
// Loading Location Rule Sagas
// ============================================================================

function* fetchLoadingLocationRulesSaga(
  action: PayloadAction<LoadingLocationRuleFilter | undefined>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocationRules.getAll,
      action.payload
    )
    const rules = transformDtosToLoadingLocationRules(response.data || [])
    yield put(queueActions.fetchLoadingLocationRulesSuccess(rules))
  } catch (error: any) {
    yield put(
      queueActions.fetchLoadingLocationRulesFailure(
        extractErrorMessage(error) || 'queue.errors.fetchRulesFailed'
      )
    )
  }
}

function* createLoadingLocationRuleSaga(
  action: PayloadAction<LoadingLocationRuleFormData>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocationRules.create,
      action.payload
    )
    const rule = transformDtoToLoadingLocationRule(response.data)
    yield put(queueActions.createLoadingLocationRuleSuccess(rule))
  } catch (error: any) {
    yield put(
      queueActions.createLoadingLocationRuleFailure(
        extractErrorMessage(error) || 'queue.errors.createRuleFailed'
      )
    )
  }
}

function* updateLoadingLocationRuleSaga(
  action: PayloadAction<{ id: string; data: Partial<LoadingLocationRuleFormData> }>
): Generator {
  try {
    const response: any = yield call(
      queueApi.loadingLocationRules.update,
      action.payload.id,
      action.payload.data
    )
    const rule = transformDtoToLoadingLocationRule(response.data)
    yield put(queueActions.updateLoadingLocationRuleSuccess(rule))
  } catch (error: any) {
    yield put(
      queueActions.updateLoadingLocationRuleFailure(
        extractErrorMessage(error) || 'queue.errors.updateRuleFailed'
      )
    )
  }
}

function* deleteLoadingLocationRuleSaga(action: PayloadAction<string>): Generator {
  try {
    yield call(queueApi.loadingLocationRules.delete, action.payload)
    yield put(queueActions.deleteLoadingLocationRuleSuccess(action.payload))
  } catch (error: any) {
    yield put(
      queueActions.deleteLoadingLocationRuleFailure(
        extractErrorMessage(error) || 'queue.errors.deleteRuleFailed'
      )
    )
  }
}

// ============================================================================
// Rule Reference Data Saga
// ============================================================================

function* fetchRuleReferenceDataSaga(): Generator {
  try {
    const [citiesList, tenantsList]: any[] = yield all([
      call([referenceDataApi, referenceDataApi.getCities]),
      call([referenceDataApi, referenceDataApi.getTenants]),
    ])

    const cities = (citiesList ?? []).map((c: any) => {
      let nameEn = c.name
      let nameAr: string | undefined

      const localizations = c.localizations?.data ?? c.localizations ?? []
      if (Array.isArray(localizations)) {
        for (const loc of localizations) {
          const attrs = loc?.attributes ?? loc
          if (attrs?.locale === 'en' && attrs?.name) nameEn = attrs.name
          if (attrs?.locale === 'ar' && attrs?.name) nameAr = attrs.name
        }
      }

      return {
        documentId: c.documentId,
        name: typeof c.name === 'object'
          ? (c.name?.en ?? c.name?.ar ?? c.documentId)
          : (c.name ?? c.documentId),
        nameEn,
        nameAr,
      }
    })

    const tenants = (tenantsList ?? []).map((t: any) => ({
      documentId: t.documentId,
      name: typeof t.name === 'object'
        ? (t.name?.en ?? t.name?.ar ?? t.documentId)
        : (t.name ?? t.documentId),
    }))

    yield put(queueActions.fetchRuleReferenceDataSuccess({ cities, tenants }))
  } catch {
    yield put(queueActions.fetchRuleReferenceDataFailure())
  }
}

// ============================================================================
// Root Saga
// ============================================================================

export function* queueSaga(): Generator {
  yield all([
    takeLatest(queueActions.fetchLoadingLocations.type, fetchLoadingLocationsSaga),
    takeLatest(queueActions.fetchLoadingLocation.type, fetchLoadingLocationSaga),
    takeLatest(queueActions.createLoadingLocation.type, createLoadingLocationSaga),
    takeLatest(queueActions.updateLoadingLocation.type, updateLoadingLocationSaga),
    takeLatest(queueActions.deleteLoadingLocation.type, deleteLoadingLocationSaga),
    takeLatest(queueActions.fetchQueueEntries.type, fetchQueueEntriesSaga),
    takeLatest(queueActions.fetchExpiredEntriesToday.type, fetchExpiredEntriesTodaySaga),
    takeLatest(queueActions.fetchQueueEntry.type, fetchQueueEntrySaga),
    takeLatest(queueActions.cancelEntry.type, cancelEntrySaga),
    takeLatest(queueActions.joinQueue.type, joinQueueSaga),
    takeLatest(queueActions.refreshQueue.type, refreshQueueSaga),
    takeLatest(queueActions.fetchDashboard.type, fetchDashboardSaga),
    takeLatest(queueActions.fetchRecentEvents.type, fetchRecentEventsSaga),
    takeLatest(queueActions.fetchReferenceData.type, fetchReferenceDataSaga),
    takeLatest(queueActions.fetchLoadingLocationRules.type, fetchLoadingLocationRulesSaga),
    takeLatest(queueActions.createLoadingLocationRule.type, createLoadingLocationRuleSaga),
    takeLatest(queueActions.updateLoadingLocationRule.type, updateLoadingLocationRuleSaga),
    takeLatest(queueActions.deleteLoadingLocationRule.type, deleteLoadingLocationRuleSaga),
    takeLatest(queueActions.fetchRuleReferenceData.type, fetchRuleReferenceDataSaga),
  ])
}
